import { type ReactNode, useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";
import { CheckOutlined } from "@ant-design/icons";
import { useBooleanFlagValue } from "@openfeature/react-sdk";
import { createStyles } from "antd-style";

import { useProvinces } from "@av/apps/recruitment/ats-common/services/lookup-entities/provinces/provinces";
import { App } from "@av/components/atoms/app/App";
import { Card } from "@av/components/atoms/Card";
import { Form } from "@av/components/atoms/form/Form";
import { Input } from "@av/components/atoms/input/Input";
import { Select } from "@av/components/atoms/Select";
import { PhoneInput } from "@av/components/molecules/PhoneInput";
import { AvaResumeNoticeCard } from "./AvaResumeNoticeCard";
import { AVA_ORB_IMAGE_PATH } from "../avaResumeAutofill.constants";
import {
  type AvaResumeAutofillField,
  type AvaResumeAutofillStatus,
  useAvaResumeAutofill,
} from "../hooks/useAvaResumeAutofill";

import type { CandidateDataUI, UploadFileExtended } from "../../../../services/apply/types";

const useStyles = createStyles(({ css, token }) => ({
  formItem: css`
    margin-bottom: ${token.marginXXS}px;
  `,
  avaFieldControl: css`
    position: relative;
    width: 100%;

    & > .ant-input,
    & > .ant-input-affix-wrapper,
    & > .ant-input-group-wrapper,
    & > .ant-select {
      width: 100%;
    }
  `,
  avaFieldControlParsing: css`
    & > .ant-input,
    & > .ant-input-affix-wrapper input,
    & > .ant-input-group-wrapper input {
      color: transparent;
      caret-color: transparent;
      -webkit-text-fill-color: transparent;
    }

    & > .ant-select .ant-select-selection-item {
      color: transparent;
    }
  `,
  avaFieldMarker: css`
    position: absolute;
    left: calc(100% + ${token.marginXS}px);
    top: 50%;
    display: flex;
    align-items: center;
    justify-content: center;
    transform: translateY(-50%);
    width: 12px;
    height: 12px;
    pointer-events: none;
  `,
  avaFieldMarkerOrb: css`
    position: absolute;
    inset: 0;
    width: 100%;
    height: 100%;
    object-fit: cover;
  `,
  avaFieldMarkerCheck: css`
    position: relative;
    color: ${token.colorWhite};
    font-size: 7px;
  `,
  avaFieldSkeleton: css`
    position: absolute;
    left: ${token.paddingSM}px;
    top: 50%;
    z-index: 1;
    width: 111px;
    height: 10px;
    transform: translateY(-50%);
    background: #e0e2ea;
    border-radius: 5px;
    pointer-events: none;
    animation: ava-resume-field-pulse 1s ease-in-out infinite alternate;

    @keyframes ava-resume-field-pulse {
      from {
        opacity: 0.35;
      }

      to {
        opacity: 1;
      }
    }

    @media (prefers-reduced-motion: reduce) {
      animation: none;
    }
  `,
  avaPhoneFieldSkeleton: css`
    left: calc(100px + ${token.paddingSM}px);
  `,
}));

const AVA_MARKER_TEST_IDS: Partial<Record<AvaResumeAutofillField, string>> = {
  firstName: "candidate-application-ava-filled-first-name",
  lastName: "candidate-application-ava-filled-last-name",
  email: "candidate-application-ava-filled-email",
  phoneNumber: "candidate-application-ava-filled-phone",
  linkedIn: "candidate-application-ava-filled-linkedin",
  subdivision: "candidate-application-ava-filled-subdivision",
  city: "candidate-application-ava-filled-city",
};

const AVA_SUCCESS_MESSAGE_KEYS: Partial<Record<AvaResumeAutofillStatus, string>> = {
  success: "recruitment.jobApplication.avaResumeNotice_successMessage",
  partial: "recruitment.jobApplication.avaResumeNotice_partialMessage",
};

type CandidateFieldConfig = {
  name: `candidate.${keyof CandidateDataUI["candidate"]}`;
  field?: AvaResumeAutofillField;
  testIdPrefix: string;
  label: string;
  required?: boolean;
  children: ReactNode;
};

export function GeneralInformationCard() {
  const { t } = useTranslation();
  const { message } = App.useApp();
  const { styles } = useStyles();
  const { dataDropDown: provincesDropdown } = useProvinces({});
  const isResumeAutofillEnabled = useBooleanFlagValue("ats_job_board_resume_autofill", false);
  const form = Form.useFormInstance<CandidateDataUI>();
  const resume = Form.useWatch("documents.resume", { form }) as UploadFileExtended | undefined;
  const avaResumeAutofill = useAvaResumeAutofill({ form, resume });
  const previousAvaStatusRef = useRef(avaResumeAutofill.status);

  useEffect(() => {
    const previousStatus = previousAvaStatusRef.current;

    previousAvaStatusRef.current = avaResumeAutofill.status;

    if (previousStatus === avaResumeAutofill.status) {
      return;
    }

    const successMessageKey = AVA_SUCCESS_MESSAGE_KEYS[avaResumeAutofill.status];

    if (successMessageKey) {
      message.success(t(successMessageKey));
    }
  }, [avaResumeAutofill.status, message, t]);

  const avaFieldMarker = (field: AvaResumeAutofillField) => {
    const testId = AVA_MARKER_TEST_IDS[field];

    if (!testId || !avaResumeAutofill.highlightedFields.has(field)) {
      return undefined;
    }

    return (
      <span className={styles.avaFieldMarker} aria-hidden="true" data-testid={testId}>
        <img className={styles.avaFieldMarkerOrb} src={AVA_ORB_IMAGE_PATH} alt="" />
        <CheckOutlined className={styles.avaFieldMarkerCheck} />
      </span>
    );
  };

  const avaFieldPlaceholder = (field: AvaResumeAutofillField, defaultPlaceholder: string) => {
    if (avaResumeAutofill.status === "parsing") return "";

    return avaResumeAutofill.unfilledFields.has(field)
      ? t("recruitment.jobApplication.avaResumeNotice_unfilledPlaceholder")
      : defaultPlaceholder;
  };

  const candidateField = ({
    name,
    field,
    testIdPrefix,
    label,
    required,
    children,
  }: CandidateFieldConfig) => {
    const isAvaFieldParsing = Boolean(field) && avaResumeAutofill.status === "parsing";

    return (
      <Form.Item
        key={name}
        testIdPrefix={testIdPrefix}
        label={label}
        required={required}
        className={styles.formItem}
      >
        <div
          className={`${styles.avaFieldControl} ${
            isAvaFieldParsing ? styles.avaFieldControlParsing : ""
          }`}
          data-ava-parsing={isAvaFieldParsing || undefined}
          inert={isAvaFieldParsing || undefined}
        >
          <Form.Item name={name} testIdPrefix={`${testIdPrefix}-field`} labelString={label} noStyle>
            {children}
          </Form.Item>
          {isAvaFieldParsing ? (
            <span
              className={`${styles.avaFieldSkeleton} ${
                field === "phoneNumber" ? styles.avaPhoneFieldSkeleton : ""
              }`}
              aria-hidden="true"
              data-testid={`${testIdPrefix}-ava-skeleton`}
            />
          ) : null}
          {field ? avaFieldMarker(field) : null}
        </div>
      </Form.Item>
    );
  };

  const candidateFields: CandidateFieldConfig[] = [
    {
      name: "candidate.firstName",
      field: "firstName",
      testIdPrefix: "candidate-application-first-name",
      label: t("recruitment.jobApplication.label_firstName"),
      required: true,
      children: (
        <Input
          size="middle"
          placeholder={avaFieldPlaceholder(
            "firstName",
            t("recruitment.jobApplication.placeholder_firstName")
          )}
          testIdPrefix="candidate-application-first-name"
        />
      ),
    },
    {
      name: "candidate.middleName",
      testIdPrefix: "candidate-application-middle-name",
      label: t("recruitment.jobApplication.label_middleName"),
      children: (
        <Input
          size="middle"
          placeholder={t("recruitment.jobApplication.placeholder_middleName")}
          testIdPrefix="candidate-application-middle-name"
        />
      ),
    },
    {
      name: "candidate.lastName",
      field: "lastName",
      testIdPrefix: "candidate-application-last-name",
      label: t("recruitment.jobApplication.label_lastName"),
      required: true,
      children: (
        <Input
          size="middle"
          placeholder={avaFieldPlaceholder(
            "lastName",
            t("recruitment.jobApplication.placeholder_lastName")
          )}
          testIdPrefix="candidate-application-last-name"
        />
      ),
    },
    {
      name: "candidate.email",
      field: "email",
      testIdPrefix: "candidate-application-email",
      label: t("recruitment.jobApplication.label_email"),
      required: true,
      children: (
        <Input
          size="middle"
          placeholder={avaFieldPlaceholder(
            "email",
            t("recruitment.jobApplication.placeholder_email")
          )}
          testIdPrefix="candidate-application-email"
        />
      ),
    },
    {
      name: "candidate.phoneNumber",
      field: "phoneNumber",
      testIdPrefix: "candidate-application-phone",
      label: t("recruitment.jobApplication.label_phoneNumber"),
      required: true,
      children: (
        <PhoneInput
          name="candidate.phoneNumber"
          testIdPrefix="candidate-application-phone"
          placeholder={avaFieldPlaceholder(
            "phoneNumber",
            t("recruitment.jobApplication.placeholder_phoneNumber")
          )}
          size="middle"
        />
      ),
    },
    {
      name: "candidate.linkedIn",
      field: "linkedIn",
      testIdPrefix: "candidate-application-linkedIn",
      label: t("recruitment.jobApplication.label_linkedIn"),
      children: (
        <Input
          size="middle"
          placeholder={avaFieldPlaceholder(
            "linkedIn",
            t("recruitment.jobApplication.placeholder_linkedIn")
          )}
          testIdPrefix="candidate-application-linkedIn"
        />
      ),
    },
    {
      name: "candidate.subdivision",
      field: "subdivision",
      testIdPrefix: "candidate-application-subdivision",
      label: t("recruitment.jobApplication.label_subdivision"),
      required: true,
      children: (
        <Select
          showSearch
          placeholder={avaFieldPlaceholder(
            "subdivision",
            t("recruitment.jobApplication.placeholder_subdivision")
          )}
          options={provincesDropdown}
          optionFilterProp="label"
          testIdPrefix="candidate-application-subdivision"
        />
      ),
    },
    {
      name: "candidate.city",
      field: "city",
      testIdPrefix: "candidate-application-city",
      label: t("recruitment.jobApplication.label_city"),
      required: true,
      children: (
        <Input
          size="middle"
          placeholder={avaFieldPlaceholder(
            "city",
            t("recruitment.jobApplication.placeholder_city")
          )}
          testIdPrefix="candidate-application-city"
        />
      ),
    },
  ];

  return (
    <Card
      title={t("recruitment.jobApplication.section_generalInformation")}
      testIdPrefix="candidate-application-general-information"
    >
      {isResumeAutofillEnabled ? (
        <AvaResumeNoticeCard
          status={avaResumeAutofill.status}
          description={avaResumeAutofill.errorMessage}
          autofilledFieldCount={avaResumeAutofill.autofilledFields.length}
          onPrimaryAction={avaResumeAutofill.requestAutofill}
          onUndo={avaResumeAutofill.undoAutofill}
          onClear={avaResumeAutofill.clearAutofilledFields}
        />
      ) : null}

      {candidateFields.map(candidateField)}
    </Card>
  );
}
